<script setup lang="ts">
import { computed } from 'vue';
import { match } from 'ts-pattern';
import { AppBadge, FontAwesomeIcon, useBillingPeriod } from '@sunlabde/m2m-library';
import { useBreakpoints } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import CardSegmentsWrapper from './CardSegmentsWrapper.vue';
import { useLocalization } from '@/models/Localization';
import { getFormattedPrice } from '@/models/Currency';
import type { PlanType, PlanTypeCard } from '@/models/PlanType';
import { globalBreakpoints } from '@/utilities/Breakpoints';
import { getInstrumentBySlug } from '@/utilities/Instrument';

const props = withDefaults(defineProps<{
	content: PlanTypeCard;
	isHighlighted?: boolean;
	isSelected?: boolean;
}>(), {
	isHighlighted: false,
	isSelected: false,
});

const billingPeriod = computed(() => match(props.content.billingOccurence)
	.with('Yearly', () => 'P1Y')
	.otherwise(() => 'P1M'));

const computedPeriod = computed(() => billingPeriod);

const { isMonthly } = useBillingPeriod(computedPeriod.value);

const { localize } = useLocalization();

const { greaterOrEqual } = useBreakpoints(globalBreakpoints);

const isGreaterOrEqualMobile = computed(() => greaterOrEqual('mobile').value);

const { t } = useI18n();

const kindOfEnumerationByPlanType = (type: PlanType) => match(type)
	.with('plus', () => t('subscribe.step.selectPlanCategory.pass.plus.instruments.and'))
	.with('single', () => t('subscribe.step.selectPlanCategory.pass.single.instrument.or'))
	.exhaustive();

const instrumentSentence = computed(() => {
	const instrumentSentenceParts = props.content.instruments.map((instrument, index) => {
		const instrumentBySlug = getInstrumentBySlug(instrument);
		if (!instrumentBySlug) return '';

		if (index === props.content.instruments.length - 2) {
			return `${localize(instrumentBySlug.displayName)} <strong>${kindOfEnumerationByPlanType(props.content.type)}</strong>`;
		}

		if (index !== props.content.instruments.length - 1) {
			return `${localize(instrumentBySlug.displayName)},`;
		}

		return localize(instrumentBySlug.displayName);
	});

	return instrumentSentenceParts.join(' ');
});
</script>

<template>
	<CardSegmentsWrapper
		:is-selected="isSelected"
		is-selectable
	>
		<template #header>
			<div class="flex justify-between items-center">
				<p class="font-serif font-bold text-xl mobile:text-2xl text-center">
					{{ localize(content.name) }}
				</p>
				<AppBadge
					v-if="content.isTrial"
					color="secondary"
					:size="isGreaterOrEqualMobile ? 'md' : 'sm'"
				>
					{{ $t('subscribe.trial') }}
				</AppBadge>
			</div>
		</template>
		<template #body>
			<div class="text-sm mobile:text-lg flex flex-col mb-4 mobile:mb-6">
				<!-- eslint-disable-next-line vue/no-v-html -->
				<p v-html="instrumentSentence" />
			</div>
			<ul class="space-y-2 mobile:space-y-4">
				<li
					v-for="(benefit, $index) in content.benefits"
					:key="$index"
					class="flex items-center text-sm mobile:text-lg"
				>
					<FontAwesomeIcon
						icon="check"
						class="text-green-300 mr-4"
					/>
					<span>{{ localize(benefit) }}</span>
				</li>
			</ul>
		</template>
		<template #footer>
			<div class="flex flex-wrap self-end items-center gap-2">
				<div
					v-if="content.isTrial"
					class="flex items-end w-full justify-center phablet:justify-start phablet:w-fit"
				>
					<p class="text-sm mobile:text-lg leading-none">
						{{ $t('subscribe.afterTrial') }}:
					</p>
				</div>
				<div
					class="flex items-center gap-2 mx-auto phablet:mr-0 phablet:ml-auto justify-center phablet:justify-start w-full phablet:w-fit"
				>
					<div
						class="font-bold leading-tight"
						:class="{ 'text-sm line-through': content.discountInPercent }"
					>
						{{
							getFormattedPrice(
								content.pricePerBillingOccurenceInCents,
								'EUR',
								{
									fromCents: true,
									precision: 2,
								},
							)
						}}
					</div>
					<AppBadge
						v-if="content.discountInPercent && content.discountInPercent > 0"
						color="green-light"
						:size="isGreaterOrEqualMobile ? 'md' : 'sm'"
					>
						-{{ content.discountInPercent }}%
					</AppBadge>
					<div
						v-if="content.discountInPercent"
						class="text-primary-500 font-bold leading-tight"
					>
						{{
							getFormattedPrice(
								content.pricePerBillingOccurenceInCents * (1 - content.discountInPercent / 100),
								'EUR',
								{
									fromCents: true,
									precision: 2,
								},
							)
						}}
					</div>
					<span class="text-base font-light">
						/{{ isMonthly ? $t('billingOccurence.month.unit') : $t('billingOccurence.annual.unit') }}
					</span>
				</div>
			</div>
		</template>
	</CardSegmentsWrapper>
</template>
